import {zodResolver} from "@hookform/resolvers/zod";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Typography,
} from "@mui/material";
import {Elements} from "@stripe/react-stripe-js";
import {useNavigate} from "@tanstack/react-router";
import BigNumber from "bignumber.js";
import {CheckCircle2} from "lucide-react";
import {type ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {CartPanel} from "@/components/Cart/CartPanel.tsx";
import useHandleFabSubmit from "@/components/Cart/useHandleFabSubmit.ts";
import CartPaymentForm, {
    buildPayAmount,
    stripePromise,
} from "@/components/Forms/CartPaymentForm.tsx";
import {getPaymentFormSchema, type PaymentFormSchemaType} from "@/components/Forms/PaymentForm.tsx";
import GlenSnackBar from "@/components/GlenSnackBar.tsx";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import {useFeeProvider} from "@/components/Providers/FeesProvider.tsx";
import {useUser} from "@/components/Providers/JWTProvider.tsx";
import {
    CONNECTED,
    READY,
    useStripeTerminal,
} from "@/components/Providers/StripeTerminalProvider.tsx";
import StandardPage from "@/components/StandardPage.tsx";
import TanMuiLink from "@/components/TanMuiLink.tsx";
import {formatter} from "@/Helpers/formatter.ts";
import {useEnrolledProgramStatus} from "@/hooks/Program/useProgramStatus.ts";
import useCashPayment from "@/hooks/useCashPayment.ts";
import useInPersonCheck from "@/hooks/useInPersonCheck.ts";
import useStripeCreateSetupIntent from "@/hooks/useStripeCreateSetupIntent.tsx";
import type {CreateSetupIntentResponse} from "@/types/CreateSetupIntentResponse.ts";
import type {SetupIntent} from "@/types/SetupIntent.tsx";
import {closeFMWindow} from "@/utils/fmScripts.ts";

const CartPage = (): ReactElement => {
    const {isEmployee, isStudentOrCM, user} = useUser();
    const {getCartAlertStatus, resetCartAlertStatus, getCartTotal, cartIsEmpty, cart} =
        useCartContext();
    const totalCost = getCartTotal();
    const cartStatus = getCartAlertStatus();
    const [isSetup, setIsSetup] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [setupIntent, setSetupIntent] = useState<SetupIntent | null>(null);
    const createSetupIntent = useStripeCreateSetupIntent();
    const {allAssessedFees} = useFeeProvider();
    const handelFabSubmit = useHandleFabSubmit();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const [submitStripeValues, setSubmitStripeValues] = useState<any | null>(null);
    const [stripeErrorMessage, setStripeErrorMessage] = useState("");
    const [acknowledgement, setAcknowledgement] = useState<{
        message: string;
        cartId: string | undefined;
        cartHasFees: boolean;
    } | null>(null);
    const processCashPayment = useCashPayment();
    const processInPersonCheckPayment = useInPersonCheck();
    const {paymentStatus, connected, capturedPayment} = useStripeTerminal();
    const cartHasFees = useMemo(() => {
        return cart?.cartItems.some(item => item.feeId !== undefined) ?? false;
    }, [cart]);
    const {data: sectionStatus} = useEnrolledProgramStatus();

    const finishInPersonPayment = useCallback(
        async (cartId: string | undefined, paidCartHasFees: boolean): Promise<void> => {
            closeFMWindow();

            if (user && "userType" in user && user.userType === "guest_user") {
                await navigate({to: `/cart-receipt/${cartId}`});
                return;
            }

            await navigate({
                to: isEmployee
                    ? "/instructor/fees"
                    : paidCartHasFees
                      ? "/fees"
                      : "/my-registrations",
                search: {purchased: "purchased"},
            });
        },
        [user, navigate, isEmployee],
    );

    const minimumPayment: number = useMemo(() => {
        if (!cart?.cartItems) {
            return 0;
        }

        const {partial, required} = cart.cartItems.reduce(
            ({required, partial}, item) => {
                if (item.itemType === "registration") {
                    const section = sectionStatus?.get(item.courseSectionId ?? "");

                    if (section?.studentOverrideCost) {
                        return {
                            partial,
                            required: required.plus(section.studentOverrideCost),
                        };
                    }
                }

                if (item.itemType === "fee") {
                    const assessedFee = allAssessedFees?.find(
                        assessedFee => assessedFee.id === item.assessedFeeId,
                    );

                    if (assessedFee) {
                        if (assessedFee.allowPartialPayment) {
                            return {
                                partial: partial.plus(assessedFee.assessedCost),
                                required,
                            };
                        }
                        return {
                            partial,
                            required: required.plus(assessedFee.assessedCost),
                        };
                    }
                }

                return {required, partial};
            },
            {
                required: BigNumber(0),
                partial: BigNumber(0),
            },
        );

        const min = required.plus(partial.dividedBy(100)).toNumber();
        if (min > 0.5) {
            return min;
        }
        if (required.plus(partial).lt(1)) {
            return required.plus(partial).toNumber();
        }

        return 0.5;
    }, [cart, allAssessedFees, sectionStatus?.get]);

    const allowPartialPayment = useMemo(() => {
        if (!cart?.cartItems) {
            return false;
        }

        return cart.cartItems.some(item => {
            if (item.itemType === "fee") {
                const assessedFee = allAssessedFees?.find(
                    assessedFee => assessedFee.id === item.assessedFeeId,
                );

                if (assessedFee?.allowPartialPayment) {
                    return true;
                }
            }

            return false;
        });
    }, [cart, allAssessedFees]);

    const initialValues = useMemo(
        () => ({
            paymentMethod: "",
            paymentType: allowPartialPayment ? "" : "full",
            partialPaymentAmount: 0,
        }),
        [allowPartialPayment],
    );

    const schema = useMemo(
        () =>
            getPaymentFormSchema(totalCost)
                .refine(
                    data => {
                        if (data.paymentType !== "partial") {
                            return true;
                        }
                        return data.partialPaymentAmount
                            ? data.partialPaymentAmount >= minimumPayment
                            : false;
                    },
                    {
                        path: ["partialPaymentAmount"],
                        message: `Payment amount must be at least 1% of the original cost (${formatter.format(minimumPayment)}).`,
                    },
                )
                .refine(
                    data => {
                        if (data.paymentMethod === "cash" && data.tendered) {
                            if (data.paymentType === "partial" && data.partialPaymentAmount) {
                                if (data.tendered < data.partialPaymentAmount) {
                                    return false;
                                }
                            } else if (data.paymentType !== "partial") {
                                if (data.tendered < totalCost) {
                                    return false;
                                }
                            }
                        }
                        return true;
                    },
                    {
                        path: ["tendered"],
                        message:
                            "Tendered amount must be greater then or equal to the payment amount.",
                    },
                ),
        [totalCost, minimumPayment],
    );

    const form = useForm<PaymentFormSchemaType>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
    });
    const paymentTypeWatch = form.watch("paymentType");
    const paymentMethodWatch = form.watch("paymentMethod");
    const partialPaymentAmountWatch = form.watch("partialPaymentAmount");
    const checkNumberWatch = form.watch("checkNumber");

    const payAmountRaw =
        paymentTypeWatch === "partial" ? (partialPaymentAmountWatch ?? 0) : totalCost;
    const payAmount = payAmountRaw > 0 ? payAmountRaw : totalCost;

    const isPayButtonDisabled =
        (!paymentTypeWatch && totalCost > 0) ||
        (!paymentMethodWatch && totalCost > 0) ||
        (paymentTypeWatch === "partial" &&
            (!partialPaymentAmountWatch || isNaN(partialPaymentAmountWatch))) ||
        (paymentMethodWatch === "terminal" &&
            (connected !== CONNECTED ||
                paymentStatus !== READY ||
                capturedPayment === null ||
                `${capturedPayment.payNowAmount}` !== `${payAmount}`)) ||
        (paymentMethodWatch === "inPersonCheck" &&
            (!checkNumberWatch || checkNumberWatch.length < 3)) ||
        isProcessing;

    useEffect(() => {
        void getSetupIntent();
        setIsSetup(true);
    }, [isSetup]);

    const getSetupIntent = async () => {
        setErrorMessage("");

        if (isSetup) {
            return;
        }

        const response = await createSetupIntent();

        if (response && response.ok) {
            const json = (await response.json()) as CreateSetupIntentResponse;

            if (json.errorCode !== 0) {
                setErrorMessage(json.message);
                return;
            }

            setSetupIntent(json.data as SetupIntent);

            return;
        }
        console.error("error getting setup intent", response);
        setErrorMessage("An unknown error has occurred.");
    };

    const handleFabSubmit = async (payNowAmount: number) => {
        setIsProcessing(true);
        try {
            await handelFabSubmit(payNowAmount);
        } finally {
            setIsProcessing(false);
        }
    };

    const acknowledgementDialog = (
        <Dialog open={acknowledgement !== null} disableEscapeKeyDown maxWidth="xs" fullWidth>
            <DialogTitle>Check the amount before closing</DialogTitle>
            <DialogContent>
                <Alert severity="warning">{acknowledgement?.message}</Alert>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    onClick={() => {
                        const pending = acknowledgement;
                        setAcknowledgement(null);
                        finishInPersonPayment(pending?.cartId, pending?.cartHasFees ?? false).catch(
                            e => {
                                console.error("error finishing in-person payment", e);
                            },
                        );
                    }}
                >
                    I have handled this
                </Button>
            </DialogActions>
        </Dialog>
    );

    if (cartIsEmpty) {
        return (
            <StandardPage>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        minHeight: "80vh",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: 1,
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            backgroundColor: "#fff",
                            px: 2,
                            pt: 2,
                            borderRadius: 1,
                        }}
                    >
                        <Box>
                            {isStudentOrCM && user.userType !== "guest_user" && (
                                <>
                                    Your cart is currently empty. You can add registrations by
                                    visiting the{" "}
                                    <TanMuiLink
                                        to="/programs"
                                        sx={{
                                            display: "inline",
                                        }}
                                    >
                                        Register section.
                                    </TanMuiLink>
                                </>
                            )}
                            {isStudentOrCM && user.userType === "guest_user" && (
                                <>
                                    Your cart is currently empty. You can add fees by visiting the{" "}
                                    <TanMuiLink
                                        to="/fees"
                                        sx={{
                                            display: "inline",
                                        }}
                                    >
                                        Fees section.
                                    </TanMuiLink>
                                </>
                            )}
                            {isEmployee && (
                                <>
                                    Your cart is currently empty. You can add fees by visiting the{" "}
                                    <TanMuiLink
                                        to="/instructor/fees"
                                        sx={{
                                            display: "inline",
                                        }}
                                    >
                                        fees section.
                                    </TanMuiLink>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
                {acknowledgementDialog}
            </StandardPage>
        );
    }

    if (totalCost === 0) {
        return (
            <StandardPage>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: {xs: "column", md: "row"},
                        px: {xs: "15px", md: "49.5px"},
                        maxHeight: {md: "80vh"},
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: 1,
                            width: {xs: "100%", md: "60%"},
                            px: 2,
                            pt: 2,
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            mb: 2,
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 3}}>
                            <CheckCircle2 color="#16A34A" size={24} />
                            <Typography variant="h5" color="black">
                                Ready for Submission
                            </Typography>
                        </Box>
                        <Alert severity="info" sx={{mb: 3}}>
                            Your cart contains only free items. No payment is required. You can
                            submit your order directly.
                        </Alert>
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                sx={{
                                    minWidth: 200,
                                }}
                                disabled={isProcessing || isPayButtonDisabled}
                                onClick={() => {
                                    handleFabSubmit(0).catch(e => {
                                        console.error("error with fab", e);
                                    });
                                }}
                            >
                                {isProcessing && (
                                    <>
                                        <CircularProgress
                                            size="1rem"
                                            sx={{
                                                mr: 1,
                                                color: "#fff",
                                            }}
                                        />{" "}
                                        Processing
                                    </>
                                )}
                                {!isProcessing && "SUBMIT ORDER"}
                            </Button>
                        </Box>
                    </Box>
                    <CartPanel
                        totalCost={totalCost}
                        cartIsEmpty={cartIsEmpty}
                        errorMessage={errorMessage}
                        isPayButtonDisabled={isPayButtonDisabled}
                        triggerPayment={() => {
                            handleFabSubmit(0).catch(e => {
                                console.error("error with fab", e);
                            });
                        }}
                        cartPage={true}
                    />
                </Box>
                {acknowledgementDialog}
            </StandardPage>
        );
    }

    return (
        <StandardPage>
            <FormProvider {...form}>
                <form
                    style={{width: "100%"}}
                    onSubmit={form.handleSubmit(async (values, event) => {
                        event?.stopPropagation();

                        if (isPayButtonDisabled) {
                            return;
                        }

                        const payNowAmount = buildPayAmount(
                            values.paymentType,
                            totalCost,
                            values.partialPaymentAmount?.toString(),
                        );

                        setStripeErrorMessage("");
                        setIsProcessing(true);
                        const cartId = cart?.id;
                        if (values.paymentMethod === "stripe") {
                            setSubmitStripeValues(values);
                        } else if (values.paymentMethod === "cash") {
                            const response = await processCashPayment(
                                BigNumber(values.tendered ?? 0)
                                    .times(100)
                                    .integerValue(BigNumber.ROUND_FLOOR)
                                    .toNumber(),
                                payNowAmount,
                            );
                            if (response.error) {
                                form.setError("tendered", {
                                    message: response.error,
                                    type: "custom",
                                });
                            } else if (response.acknowledgement) {
                                setAcknowledgement({
                                    message: response.acknowledgement,
                                    cartId,
                                    cartHasFees,
                                });
                            } else {
                                await finishInPersonPayment(cartId, cartHasFees);
                            }
                            setIsProcessing(false);
                        } else if (values.paymentMethod === "inPersonCheck") {
                            const response = await processInPersonCheckPayment(
                                values.checkNumber ?? "",
                                payNowAmount,
                            );
                            if (response.error) {
                                form.setError("checkNumber", {
                                    message: response.error,
                                    type: "custom",
                                });
                            } else if (response.acknowledgement) {
                                setAcknowledgement({
                                    message: response.acknowledgement,
                                    cartId,
                                    cartHasFees,
                                });
                            } else {
                                await finishInPersonPayment(cartId, cartHasFees);
                            }
                            setIsProcessing(false);
                        }
                    })}
                >
                    <Box
                        sx={{
                            width: "100%",
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: {xs: "column", md: "row"},
                            px: {xs: "15px", md: "49.5px"},
                            maxHeight: {md: "80vh"},
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                width: {xs: "100%", md: "60%"},
                                px: 2,
                                pt: 2,
                                overflowY: "auto",
                                backgroundColor: "#fff",
                                mb: 2,
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant={"h5"}
                                sx={{
                                    pt: 1,
                                    pb: 1,
                                    color: "#000",
                                }}
                            >
                                Payment Method
                            </Typography>
                            <Divider />

                            {setupIntent && (
                                <Elements
                                    stripe={stripePromise}
                                    options={{
                                        clientSecret: setupIntent.clientSecret,
                                        appearance: {
                                            theme: "stripe",
                                            variables: {
                                                fontWeightNormal: "500",
                                                borderRadius: "8px",
                                                colorPrimary: "#000",
                                                tabIconSelectedColor: "#fff",
                                                gridRowSpacing: "16px",
                                            },
                                            rules: {
                                                ".Tab, .Input, .Block, .CheckboxInput, .CodeInput":
                                                    {
                                                        boxShadow:
                                                            "0px 3px 10px rgba(18, 42, 66, 0.08)",
                                                    },
                                                ".BlockDivider": {
                                                    backgroundColor: "#ebebeb",
                                                },
                                                ".Tab--selected, .Tab--selected:hover": {
                                                    backgroundColor: "#631d79",
                                                    color: "#fff",
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <CartPaymentForm
                                        cost={totalCost}
                                        allowPartialPayment={allowPartialPayment}
                                        isProcessing={isProcessing}
                                        setIsProcessing={setIsProcessing}
                                        stripeErrorMessage={stripeErrorMessage}
                                        setStripeErrorMessage={setStripeErrorMessage}
                                        submitStripeValues={submitStripeValues}
                                        setSubmitStripeValues={setSubmitStripeValues}
                                        setupIntent={setupIntent}
                                        cartHasFees={cartHasFees}
                                        isPayButtonDisabled={isPayButtonDisabled}
                                    />
                                </Elements>
                            )}
                        </Paper>
                        <CartPanel
                            totalCost={payAmount}
                            cartIsEmpty={cartIsEmpty}
                            errorMessage={errorMessage}
                            isPayButtonDisabled={isPayButtonDisabled}
                            triggerPayment={() => {
                                form.handleSubmit(() => {});
                            }}
                            cartPage={true}
                        />
                    </Box>
                </form>
            </FormProvider>
            <GlenSnackBar
                onClose={resetCartAlertStatus}
                show={cartStatus.showAlert}
                severity={cartStatus.alertVariant}
                message={cartStatus.message}
            />
            {acknowledgementDialog}
        </StandardPage>
    );
};

export default CartPage;
